import {
    FeatureFlags,
    type CreateUserAttribute,
    type UserAttribute,
} from '@lightdash/common';
import {
    ActionIcon,
    Button,
    Group,
    Select,
    Stack,
    Switch,
    Text,
    Textarea,
    TextInput,
    Tooltip,
} from '@mantine-8/core';
import { useForm } from '@mantine/form';
import {
    IconInfoCircle,
    IconPlus,
    IconTrash,
    IconUserPlus,
    IconUsersPlus,
} from '@tabler/icons-react';
import { useEffect, useState, type FC } from 'react';
import { useOrganizationGroups } from '../../../hooks/useOrganizationGroups';
import { useOrganizationUsers } from '../../../hooks/useOrganizationUsers';
import { useServerFeatureFlag } from '../../../hooks/useServerOrClientFeatureFlag';
import {
    useCreateUserAtributesMutation,
    useUpdateUserAtributesMutation,
} from '../../../hooks/useUserAttributes';
import MantineIcon from '../../common/MantineIcon';
import MantineModal from '../../common/MantineModal';

const UserAttributeModal: FC<{
    opened: boolean;
    userAttribute?: UserAttribute;
    allUserAttributes: UserAttribute[];
    onClose: () => void;
}> = ({ opened, userAttribute, allUserAttributes, onClose }) => {
    const userGroupsFeatureFlagQuery = useServerFeatureFlag(
        FeatureFlags.UserGroupsEnabled,
    );

    const form = useForm<CreateUserAttribute>({
        initialValues: {
            name: userAttribute?.name || '',
            description: userAttribute?.description,
            users: userAttribute?.users || [],
            groups: userAttribute?.groups || [],
            attributeDefault: userAttribute?.attributeDefault || null,
        },
        validate: {
            name: (value: string) => {
                if (!/^[a-z_][a-z0-9_]*$/.test(value)) {
                    return `Invalid attribute name. Attribute name must contain only lowercase characters, '_' or numbers and it can't start with a number`;
                }
                if (
                    allUserAttributes.some(
                        (attr) =>
                            attr.name === value &&
                            attr.uuid !== userAttribute?.uuid,
                    )
                ) {
                    return `Attribute with the same name already exists`;
                }
                return null;
            },
            users: (value: { userUuid: string; value: string }[]) => {
                if (
                    value.reduceRight(
                        (acc, user, index) =>
                            acc ||
                            value.some(
                                (otherUser, otherIndex) =>
                                    index !== otherIndex &&
                                    user.userUuid === otherUser.userUuid,
                            ),
                        false,
                    )
                ) {
                    return `Duplicated users`;
                }
                return null;
            },
            groups: (value: { groupUuid: string; value: string }[]) => {
                if (
                    value.reduceRight(
                        (acc, group, index) =>
                            acc ||
                            value.some(
                                (otherGroup, otherIndex) =>
                                    index !== otherIndex &&
                                    group.groupUuid === otherGroup.groupUuid,
                            ),
                        false,
                    )
                ) {
                    return `Duplicated groups`;
                }
                return null;
            },
        },
    });
    const [inputError, setInputError] = useState<string | undefined>();
    const { mutate: createUserAttribute } = useCreateUserAtributesMutation();
    const { mutate: updateUserAttribute } = useUpdateUserAtributesMutation(
        userAttribute?.uuid,
    );
    const [checked, setChecked] = useState(false);

    useEffect(() => {
        //Reset checked on edit
        setChecked(
            userAttribute?.attributeDefault !== undefined &&
                userAttribute?.attributeDefault !== null,
        );
    }, [userAttribute?.attributeDefault]);

    const handleClose = () => {
        form.reset();
        setInputError(undefined);
        setChecked(false);
        if (onClose) onClose();
    };
    const handleSubmit = (data: CreateUserAttribute) => {
        if (userAttribute?.uuid) {
            updateUserAttribute(data);
        } else {
            createUserAttribute(data);
        }
        handleClose();
    };

    if (userGroupsFeatureFlagQuery.isError) {
        console.error(userGroupsFeatureFlagQuery.error);
        throw new Error('Error fetching user groups feature flag');
    }

    const isGroupManagementEnabled =
        userGroupsFeatureFlagQuery.isSuccess &&
        userGroupsFeatureFlagQuery.data.enabled;

    const { data: orgUsers } = useOrganizationUsers();
    const { data: groups } = useOrganizationGroups(
        {},
        { enabled: isGroupManagementEnabled },
    );

    return (
        <MantineModal
            opened={opened}
            onClose={handleClose}
            title={`${userAttribute ? 'Update' : 'Add'} user attribute`}
            icon={IconPlus}
            size="lg"
            actions={
                <Button type="submit" form="add_user_attribute">
                    {userAttribute ? 'Update' : 'Add'}
                </Button>
            }
        >
            <form
                id="add_user_attribute"
                name="add_user_attribute"
                onSubmit={form.onSubmit((values: CreateUserAttribute) =>
                    handleSubmit(values),
                )}
            >
                <Stack gap="xs">
                    <TextInput
                        name="name"
                        label="Attribute name"
                        placeholder="E.g. user_country"
                        required
                        {...form.getInputProps('name')}
                    />
                    <Text c="red" size="sm">
                        {inputError}
                    </Text>

                    <Textarea
                        name="description"
                        label="Description"
                        placeholder="E.g. The country where the user is querying data from."
                        {...form.getInputProps('description')}
                    />
                    <Stack gap="xxs">
                        <Group gap="xs">
                            <Text fw={500}>Default value</Text>

                            <Tooltip
                                position="right"
                                multiline
                                maw={200}
                                label="This will be the default value for the attribute if no other value is assigned to the user or group."
                            >
                                <MantineIcon
                                    color="gray.6"
                                    icon={IconInfoCircle}
                                />
                            </Tooltip>
                        </Group>

                        <Group h={36}>
                            <Switch
                                checked={checked}
                                onChange={(event) => {
                                    const isChecked =
                                        event.currentTarget.checked;
                                    setChecked(isChecked);
                                    if (!isChecked)
                                        form.setFieldValue(
                                            'attributeDefault',
                                            null,
                                        );
                                }}
                            />
                            {checked && (
                                <TextInput
                                    size="xs"
                                    name={`attributeDefault`}
                                    placeholder="E.g. US"
                                    required
                                    {...form.getInputProps('attributeDefault')}
                                />
                            )}
                        </Group>
                    </Stack>
                    <Stack>
                        <Stack gap="xs">
                            <Text fw={500}>Assign to users</Text>
                            {!form.isValid('users') && (
                                <Text c="red" size="xs">
                                    {form.errors.users}
                                </Text>
                            )}

                            {form.values.users?.map((user, index) => {
                                return (
                                    <Group key={index}>
                                        <Select
                                            size="xs"
                                            flex={1}
                                            label={
                                                index === 0
                                                    ? 'User email'
                                                    : undefined
                                            }
                                            name={`users.${index}.userUuid`}
                                            placeholder="E.g. test@lightdash.com"
                                            required
                                            searchable
                                            {...form.getInputProps(
                                                `users.${index}.userUuid`,
                                            )}
                                            data={
                                                orgUsers?.map((orgUser) => ({
                                                    value: orgUser.userUuid,
                                                    label: orgUser.email,
                                                })) || []
                                            }
                                        />

                                        <TextInput
                                            size="xs"
                                            flex={1}
                                            label={
                                                index === 0
                                                    ? 'Value'
                                                    : undefined
                                            }
                                            name={`users.${index}.value`}
                                            placeholder="E.g. US"
                                            required
                                            {...form.getInputProps(
                                                `users.${index}.value`,
                                            )}
                                        />
                                        <ActionIcon
                                            mt={index === 0 ? 20 : undefined}
                                            color="red"
                                            variant="outline"
                                            onClick={() => {
                                                form.setFieldValue(
                                                    'users',
                                                    form.values.users.filter(
                                                        (_, i) => i !== index,
                                                    ),
                                                );
                                            }}
                                        >
                                            <MantineIcon icon={IconTrash} />
                                        </ActionIcon>
                                    </Group>
                                );
                            })}
                            <Button
                                size="xs"
                                variant="default"
                                style={{ alignSelf: 'flex-start' }}
                                leftSection={
                                    <MantineIcon icon={IconUserPlus} />
                                }
                                onClick={() => {
                                    form.setFieldValue('users', [
                                        ...(form.values.users || []),
                                        { userUuid: '', value: '' },
                                    ]);
                                }}
                            >
                                Add user
                            </Button>
                        </Stack>

                        {isGroupManagementEnabled && (
                            <Stack gap="xs">
                                <Text fw={500}>Assign to groups</Text>
                                {!form.isValid('groups') && (
                                    <Text c="red" size="xs">
                                        {form.errors.groups}
                                    </Text>
                                )}
                                {form.values.groups.map((group, index) => {
                                    return (
                                        <Group key={index}>
                                            <Select
                                                size="xs"
                                                flex={1}
                                                label={
                                                    index === 0
                                                        ? 'Group name'
                                                        : undefined
                                                }
                                                name={`groups.${index}.groupUuid`}
                                                placeholder="E.g. Marketing, Product"
                                                required
                                                searchable
                                                {...form.getInputProps(
                                                    `groups.${index}.groupUuid`,
                                                )}
                                                data={
                                                    groups?.map(
                                                        (groupInfo) => ({
                                                            value: groupInfo.uuid,
                                                            label: groupInfo.name,
                                                        }),
                                                    ) || []
                                                }
                                            />

                                            <TextInput
                                                size="xs"
                                                flex={1}
                                                label={
                                                    index === 0
                                                        ? 'Value'
                                                        : undefined
                                                }
                                                name={`groups.${index}.value`}
                                                placeholder="E.g. US"
                                                required
                                                {...form.getInputProps(
                                                    `groups.${index}.value`,
                                                )}
                                            />
                                            <ActionIcon
                                                mt={
                                                    index === 0 ? 20 : undefined
                                                }
                                                color="red"
                                                variant="outline"
                                                onClick={() => {
                                                    form.setFieldValue(
                                                        'groups',
                                                        form.values.groups.filter(
                                                            (_, i) =>
                                                                i !== index,
                                                        ),
                                                    );
                                                }}
                                            >
                                                <MantineIcon icon={IconTrash} />
                                            </ActionIcon>
                                        </Group>
                                    );
                                })}
                                <Button
                                    size="xs"
                                    variant="default"
                                    style={{ alignSelf: 'flex-start' }}
                                    leftSection={
                                        <MantineIcon icon={IconUsersPlus} />
                                    }
                                    onClick={() => {
                                        form.insertListItem('groups', {
                                            groupUuid: '',
                                            value: '',
                                        });
                                    }}
                                >
                                    Add group
                                </Button>
                            </Stack>
                        )}
                    </Stack>
                </Stack>
            </form>
        </MantineModal>
    );
};

export default UserAttributeModal;
